/**
 * Surface Events Logger (SE-4010)
 *
 * Handles logging of engagement-surface view events to PostHog Analytics:
 * home (dashboard successor), activity feed, watchlist, roster, and
 * recommendations.
 * @module features/analytics/utils/surfaceEventLogger
 */
import { ANALYTICS_EVENTS } from "../constants";
import type {
  ISurfaceAnalyticsService,
  TActivityFeedViewedParams,
  THomeViewedParams,
  TRecommendationsViewedParams,
  TRosterViewedParams,
  TWatchlistViewedParams,
} from "../types";
import { sendAnalyticsEvent } from "./posthogSender";

/**
 * Surface events analytics logger.
 *
 * Provides methods for logging engagement-surface views with feature
 * params (layout, filter, category) that route-level screen_view
 * cannot express.
 * @example
 * ```typescript
 * import { AnalyticsTracker } from '@/features/analytics';
 *
 * AnalyticsTracker.surface.logHomeViewed({ layout: 'desktop' });
 * ```
 */
export const surfaceEventLogger: ISurfaceAnalyticsService = {
  /**
   * Logs a home viewed event.
   * @param params - Home viewed parameters
   */
  logHomeViewed: async (params: THomeViewedParams): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.SURFACE.HOME_VIEWED, params);
  },

  /**
   * Logs an activity feed viewed event.
   * @param params - Activity feed viewed parameters
   */
  logActivityFeedViewed: async (
    params: TActivityFeedViewedParams
  ): Promise<void> => {
    await sendAnalyticsEvent(
      ANALYTICS_EVENTS.SURFACE.ACTIVITY_FEED_VIEWED,
      params
    );
  },

  /**
   * Logs a watchlist viewed event.
   * @param params - Watchlist viewed parameters
   */
  logWatchlistViewed: async (params: TWatchlistViewedParams): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.SURFACE.WATCHLIST_VIEWED, params);
  },

  /**
   * Logs a roster viewed event.
   * @param params - Roster viewed parameters
   */
  logRosterViewed: async (params: TRosterViewedParams): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.SURFACE.ROSTER_VIEWED, params);
  },

  /**
   * Logs a recommendations viewed event.
   * @param params - Recommendations viewed parameters
   */
  logRecommendationsViewed: async (
    params: TRecommendationsViewedParams
  ): Promise<void> => {
    await sendAnalyticsEvent(
      ANALYTICS_EVENTS.SURFACE.RECOMMENDATIONS_VIEWED,
      params
    );
  },
};
